import {
  Configuration as GameDataConfiguration,
  GetKenoConfigRequest,
  KenoApi as GameDataKenoApi,
  KenoTicket,
  KenoTicketResult,
  ScoreTicketRequest,
  KenoDrawingResult,
  GetKenoHotAndColdNumbersRequest,
  KenoConfig,
} from '@interaction-gaming/game-data-service-browser-client'
import {
  Configuration as PlayerDataConfiguration,
  KenoApi as PlayerDataKenoApi,
  KenoPreferences,
  ResponseError as PlayerDataResponseError,
} from '@interaction-gaming/player-data-service-browser-client'
import { TicketInfo } from '@Config/ticket-form-types'
import {
  ScoredDrawResult,
  ScoredDrawResultComplete,
  ScoredDrawResultPending,
  TicketResult,
} from '@API/game-results/types'
import { mapDrawResponseToMiniGameAddOn, mapDrawResponseToMultiplierAddOn } from './rapid-games-api'
import { ExtraDrawsAddOnResult } from '@Config/draw-results-types'
import { store } from '@AppState/redux-store'

type APIConfig = {
  apiKey: string
  basePath: string
  clientId: string
}

export type GameDataConfig = APIConfig & {
  rapidGameId: string
}

export type PlayerDataConfig = APIConfig

let GAME_DATA_API_CONFIG: GameDataConfig
let PLAYER_DATA_API_CONFIG: PlayerDataConfig
let GAME_DATA_KENO_API: GameDataKenoApi
let PLAYER_DATA_KENO_API: PlayerDataKenoApi

const getGameDataApi = () => {
  if (!GAME_DATA_KENO_API) {
    throw new Error('Keno API has yet to be defined')
  }
  return GAME_DATA_KENO_API
}

const getPlayerDataApi = () => {
  if (!PLAYER_DATA_KENO_API) {
    throw new Error('Keno API has yet to be defined')
  }
  return PLAYER_DATA_KENO_API
}

const configureAPIs = (config: { gameData: GameDataConfig; playerData: PlayerDataConfig }) => {
  GAME_DATA_API_CONFIG = config.gameData
  const gameDataConfig = new GameDataConfiguration(config.gameData)
  GAME_DATA_KENO_API = new GameDataKenoApi(gameDataConfig)

  PLAYER_DATA_API_CONFIG = config.playerData
  const playerDataConfig = new PlayerDataConfiguration(config.playerData)
  PLAYER_DATA_KENO_API = new PlayerDataKenoApi(playerDataConfig)
}

const fetchKenoConfiguration = async () => {
  const api = getGameDataApi()
  const config: GetKenoConfigRequest = {
    rapidGameId: GAME_DATA_API_CONFIG.rapidGameId,
    xClientID: GAME_DATA_API_CONFIG.clientId,
  }

  return await api.getKenoConfig(config)
}

export const getKenoConfigurationForDrawNumber = (
  config: KenoConfig[] | null,
  drawNumber: number,
): KenoConfig | undefined => {
  if (!config) {
    return undefined
  }

  const applicableRules = config.find((rule) => {
    const ruleMin = rule.activeDrawNumberRange.min
    const ruleMax = rule.activeDrawNumberRange.max

    return ruleMin <= drawNumber && ((!!ruleMax && ruleMax >= drawNumber) || !ruleMax)
  })

  return applicableRules
}

export const mapScoredDrawResponseToExtraDrawingAddOn = (
  result: KenoDrawingResult,
): ExtraDrawsAddOnResult | undefined => {
  const configuredExtraDrawAddOn = getKenoConfigurationForDrawNumber(
    store.getState().gameConfig.gameRules,
    result.drawingNumber,
  )?.supportedAddOns.extraDrawAddon
  if (!configuredExtraDrawAddOn) {
    return undefined
  }

  const addOnResult = result.resultSummary?.winningsByNumber.filter(
    (winnings) => winnings.addonName === configuredExtraDrawAddOn.name,
  )

  if (!addOnResult) return undefined

  return {
    name: configuredExtraDrawAddOn.name,
    results: addOnResult.map((res) => {
      return {
        result: res.numberSelection,
        totalWinningsSoFar: res.cumulativeTicketWinningsInCents,
        ticketWinningsSoFar: res.cumulativeDrawingWinningsInCents,
      }
    }),
  }
}

const mapResponseToTicketResult = (response: KenoTicketResult): TicketResult => {
  const scoredDrawResults: ScoredDrawResult[] = response.drawingResults.map((res) => {
    if (!res.drawingComplete || !res.drawingDetails) {
      const pendingResult: ScoredDrawResultPending = {
        drawingComplete: false,
        drawingNumber: res.drawingNumber,
      }

      return pendingResult
    }

    const completeResult: ScoredDrawResultComplete = {
      drawingComplete: true,
      miniGameAddOnResult: mapDrawResponseToMiniGameAddOn(res.drawingDetails),
      multiplierAddOnResult: mapDrawResponseToMultiplierAddOn(res.drawingDetails),
      extraDrawAddOnResult: mapScoredDrawResponseToExtraDrawingAddOn(res),
      drawingDate: res.drawingDetails.result.drawingDate.toISOString(),
      drawingNumber: res.drawingNumber,
      jackpot: res.drawingDetails.jackpot.jackpot?.jackpotAmountInCents ?? null,
      scoringBreakdown: res.scoringBreakdown || null,
      totalWinningsInCents: res.totalWinningsInCents,
      winDetails: [],
      winningNumbers: res.resultSummary
        ? res.resultSummary.winningsByNumber.map((summary) => {
            return {
              cumulativeDrawingWinningsInCents: summary.cumulativeDrawingWinningsInCents,
              cumulativeTicketWinningsInCents: summary.cumulativeTicketWinningsInCents,
              addonName: summary.addonName,
              numberSelection: summary.numberSelection,
            }
          })
        : [],
    }

    return completeResult
  })

  return {
    scoredDrawResults: scoredDrawResults,
    ticketComplete: response.ticketComplete,
    totalWinningsInCents: response.totalWinningsInCents,
  }
}

const mapTicketInfoToKenoTicket = (ticketInfo: TicketInfo) => {
  return {
    addonSelection: {
      extraDrawAddon: ticketInfo.extraDrawAddOnSelection
        ? ticketInfo.extraDrawAddOnSelection.name
        : undefined,
      miniGameAddon: ticketInfo.miniGameAddOnSelection
        ? {
            name: ticketInfo.miniGameAddOnSelection.name,
            numberSelections: ticketInfo.miniGameAddOnSelection.numberSelections,
          }
        : undefined,
      multiplierAddon: ticketInfo.multiplierAddOnSelection
        ? ticketInfo.multiplierAddOnSelection.name
        : undefined,
    },
    drawingRange: {
      min: ticketInfo.startingDrawNumber,
      max: ticketInfo.startingDrawNumber + ticketInfo.numberOfDraws - 1,
    },
    numberSelections: ticketInfo.numberSelections,
    wagerInCents: ticketInfo.baseWager,
  }
}

const fetchKenoTicketResult = async (kenoTicket: KenoTicket) => {
  const api = getGameDataApi()

  const config: ScoreTicketRequest = {
    rapidGameId: GAME_DATA_API_CONFIG.rapidGameId,
    xClientID: GAME_DATA_API_CONFIG.clientId,
    kenoTicket: kenoTicket,
  }

  try {
    const result = await api.scoreTicket(config)

    return mapResponseToTicketResult(result)
  } catch (e) {
    console.error(
      `Error fetching scored ticket for draws ${kenoTicket.drawingRange.min} - ${kenoTicket.drawingRange.max}`,
      e,
    )
  }
}

const fetchTicketResult = async (ticketInfo: TicketInfo) => {
  const kenoTicket: KenoTicket = mapTicketInfoToKenoTicket(ticketInfo)
  return fetchKenoTicketResult(kenoTicket)
}

const fetchPreferences = async (
  deviceId?: string,
  playerId?: string,
): Promise<KenoPreferences | undefined> => {
  try {
    return await getPlayerDataApi().getKenoPreferences({
      xDeviceID: deviceId,
      xPlayerID: playerId,
      xClientID: PLAYER_DATA_API_CONFIG.clientId,
    })
  } catch (e) {
    if (e instanceof PlayerDataResponseError && e.response.status === 404) {
      // Not having any remote settings is a normal state
      return
    }

    throw e
  }
}

const savePreferences = async (
  preferences: KenoPreferences,
  deviceId?: string,
  playerId?: string,
): Promise<KenoPreferences> => {
  return await getPlayerDataApi().saveKenoPreferences({
    kenoPreferences: preferences,
    xDeviceID: deviceId,
    xPlayerID: playerId,
    xClientID: PLAYER_DATA_API_CONFIG.clientId,
  })
}

const fetchTicketHistory = async (deviceId?: string, playerId?: string) => {
  return await getPlayerDataApi().getKenoTickets({
    xDeviceID: deviceId,
    xPlayerID: playerId,
    xClientID: PLAYER_DATA_API_CONFIG.clientId,
  })
}

const saveTicket = async (ticketInfo: TicketInfo, deviceId?: string, playerId?: string) => {
  return await getPlayerDataApi().saveKenoTicket({
    kenoTicket: mapTicketInfoToKenoTicket(ticketInfo),
    xDeviceID: deviceId,
    xPlayerID: playerId,
    xClientID: PLAYER_DATA_API_CONFIG.clientId,
  })
}

const updateTicket = async (ticketInfo: TicketInfo, deviceId?: string, playerId?: string) => {
  if (!ticketInfo.id) {
    throw Error('Cant update ticket without id')
  }

  return await getPlayerDataApi().updateKenoTicket({
    savedTicketId: ticketInfo.id,
    kenoTicket: mapTicketInfoToKenoTicket(ticketInfo),
    xDeviceID: deviceId,
    xPlayerID: playerId,
    xClientID: PLAYER_DATA_API_CONFIG.clientId,
  })
}

const fetchHotColdNumbers = async (
  numDraws: number,
  numHotNumbers: number = 10,
  numColdNumbers: number = 10,
  drawNumber?: number,
) => {
  const api = getGameDataApi()

  const params: GetKenoHotAndColdNumbersRequest = {
    rapidGameId: GAME_DATA_API_CONFIG.rapidGameId,
    xClientID: GAME_DATA_API_CONFIG.clientId,
    numDraws,
    numHotNumbers,
    numColdNumbers,
    drawNumber,
  }

  const result = await api.getKenoHotAndColdNumbers(params)
  return result
}

export const KenoDataServiceAPI = {
  configureAPIs,
  fetchKenoConfiguration,
  fetchKenoTicketResult,
  fetchTicketResult,
  fetchPreferences,
  fetchTicketHistory,
  savePreferences,
  saveTicket,
  updateTicket,
  fetchHotColdNumbers,
}
